import React, { useState } from 'react';
import { api } from '../api/client';

export const CASH_TYPES = [
  { value: 'deposit', label: 'Deposit' },
  { value: 'withdrawal', label: 'Withdrawal' },
  { value: 'dividend', label: 'Dividend' },
  { value: 'interest', label: 'Interest' },
  { value: 'fee', label: 'Fee' },
  { value: 'opening_balance', label: 'Opening Balance' },
];
export const cashTypeLabel = (type) => CASH_TYPES.find(t => t.value === type)?.label || type;
// Amounts are stored positive; withdrawals and fees reduce cash.
export const cashSign = (type) => (type === 'withdrawal' || type === 'fee' ? -1 : 1);

const today = () => new Date().toISOString().split('T')[0];

const toForm = (entry) => ({
  account_id: entry.account_id ?? '',
  type: entry.type || 'deposit',
  date: entry.date || today(),
  amount: entry.amount ?? '',
  symbol: entry.symbol || '',
  notes: entry.notes || '',
});

// Add/edit a cash entry. `entry` is an existing row to edit, or defaults for a new
// one. `balances` (from /cash/balances) enforces one opening balance per account:
// a second one can't be saved, and the user is offered the existing one to edit;
// `lockAccount` fixes the account (e.g. opened from an Accounts row); `hint` is an
// optional note shown above the actions.
export default function CashEntryModal({ entry: initialEntry = {}, accounts, balances = {}, lockAccount, hint, onClose, onSaved }) {
  // The entry being edited can switch to the account's existing opening balance.
  const [entry, setEntry] = useState(initialEntry);
  const [form, setForm] = useState(() => toForm(initialEntry));
  const [error, setError] = useState('');
  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const editingSameOpening = entry.id && entry.type === 'opening_balance'
    && String(entry.account_id) === String(form.account_id);
  const duplicateOpening = form.type === 'opening_balance' && form.account_id
    && balances[form.account_id]?.has_opening_balance && !editingSameOpening;

  const editExistingOpening = async () => {
    try {
      const [existing] = await api.getCash({ account_id: form.account_id, type: 'opening_balance' });
      if (!existing) return setError('Could not find the existing opening balance.');
      setEntry(existing);
      setForm(toForm(existing));
      setError('');
    } catch (e) {
      setError(e.message);
    }
  };

  const submit = async () => {
    const amount = parseFloat(form.amount);
    if (duplicateOpening) return;
    if (!form.account_id) return setError('Select an account.');
    // An opening balance may be $0.00 (an account funded later by a deposit).
    if (form.type === 'opening_balance' ? !(amount >= 0) : !(amount > 0)) {
      return setError(form.type === 'opening_balance' ? 'Enter the opening balance (0 is allowed).' : 'Amount must be greater than zero.');
    }
    const payload = {
      account_id: parseInt(form.account_id),
      type: form.type,
      date: form.date,
      amount,
      symbol: form.type === 'dividend' ? form.symbol : null,
      notes: form.notes || null,
    };
    try {
      if (entry.id) await api.updateCash(entry.id, payload);
      else await api.createCash(payload);
    } catch (e) {
      return setError(e.message);
    }
    onSaved();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-title">{entry.id ? 'Edit Cash Entry' : 'Add Cash Entry'}</div>
        <div className="form-group">
          <label>Account</label>
          <select value={form.account_id} disabled={lockAccount} onChange={e => set('account_id', e.target.value)}>
            <option value="">Select account</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label>Type</label>
            <select value={form.type} onChange={e => set('type', e.target.value)}>
              {CASH_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Date</label>
            <input type="date" value={form.date} onChange={e => set('date', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Amount</label>
            <input type="number" min="0" step="0.01" value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="0.00" />
          </div>
          {form.type === 'dividend' && (
            <div className="form-group">
              <label>Paid By (symbol)</label>
              <input value={form.symbol} onChange={e => set('symbol', e.target.value.toUpperCase())} placeholder="SPAXX" />
            </div>
          )}
        </div>
        <div className="form-group">
          <label>Notes (optional)</label>
          <input value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
        {duplicateOpening && (
          <div className="loss" style={{ fontSize: 12, marginBottom: 12 }}>
            This account already has an opening balance — an account can only have one.{' '}
            <button className="btn-ghost" style={{ fontSize: 12, padding: '2px 6px' }} onClick={editExistingOpening}>
              Edit existing opening balance
            </button>
          </div>
        )}
        {hint && !entry.id && <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>{hint}</div>}
        {error && <div className="loss" style={{ fontSize: 12, marginBottom: 12 }}>{error}</div>}
        <div className="modal-actions">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn" onClick={submit} disabled={duplicateOpening}>{entry.id ? 'Save Changes' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
